/**
 * Add Rule Modal
 * Form to create dependency rules between entities
 */

import React, { useState } from 'react';
import useStore from '../store/useStore';
import './AddRuleModal.css';

const AddRuleModal = ({ onClose }) => {
  const { entities, createRule } = useStore();
  const [formData, setFormData] = useState({
    Title: '',
    SourceEntityId: '',
    TargetEntityId: '',
    RuleType: 'Required',
    ConditionExpression: '',
    ImpactValue: '',
    ImpactUnit: 'Absolute',
    Priority: 10,
    Description: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-generate title if empty
    if (name === 'SourceEntityId' || name === 'TargetEntityId' || name === 'RuleType') {
      const source = entities.find(e => e.Id === parseInt(name === 'SourceEntityId' ? value : formData.SourceEntityId));
      const target = entities.find(e => e.Id === parseInt(name === 'TargetEntityId' ? value : formData.TargetEntityId));
      const ruleType = name === 'RuleType' ? value : formData.RuleType;
      
      if (source && target && ruleType && !formData.Title) {
        const autoTitle = `${source.Title} ${ruleType.toLowerCase()} ${target.Title}`;
        setFormData(prev => ({ ...prev, Title: autoTitle }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.Title.trim()) {
      newErrors.Title = 'Rule title is required';
    }
    
    if (!formData.SourceEntityId) {
      newErrors.SourceEntityId = 'Source entity is required';
    }
    
    if (!formData.TargetEntityId) {
      newErrors.TargetEntityId = 'Target entity is required';
    }
    
    if (formData.SourceEntityId === formData.TargetEntityId) {
      newErrors.TargetEntityId = 'Source and target must be different';
    }
    
    if (!formData.ConditionExpression.trim()) {
      newErrors.ConditionExpression = 'Condition expression is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const ruleData = {
        Title: formData.Title,
        SourceEntityIdLookupId: parseInt(formData.SourceEntityId),
        TargetEntityIdLookupId: parseInt(formData.TargetEntityId),
        RuleType: formData.RuleType,
        ConditionExpression: formData.ConditionExpression,
        ImpactUnit: formData.ImpactUnit,
        Priority: parseInt(formData.Priority) || 10,
        IsActive: true,
        Description: formData.Description
      };

      // Add impact value if provided
      if (formData.ImpactValue) {
        ruleData.ImpactValue = parseFloat(formData.ImpactValue);
      }

      await createRule(ruleData);
      onClose();
    } catch (error) {
      alert('Failed to create rule. Please try again.');
      console.error('Error creating rule:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sourceEntity = entities.find(e => e.Id === parseInt(formData.SourceEntityId));
  const targetEntity = entities.find(e => e.Id === parseInt(formData.TargetEntityId));

  // Example condition expressions
  const exampleExpressions = {
    'Status': [
      "Status == 'Met'",
      "Status == 'Completed'",
      "Status != 'Blocked'"
    ],
    'Score': [
      "NumericValue >= 0.8",
      "NumericValue < 0.5",
      "NumericValue >= 1.0"
    ],
    'Boolean': [
      "BooleanValue == true",
      "BooleanValue == false"
    ],
    'Combined': [
      "Status == 'Met' && NumericValue >= 0.9",
      "Status != 'Blocked' && NumericValue > 0"
    ]
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Dependency Rule</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="rule-form">
          <div className="form-group">
            <label htmlFor="Title">Rule Title *</label>
            <input
              type="text"
              id="Title"
              name="Title"
              value={formData.Title}
              onChange={handleChange}
              placeholder="e.g., Production depends on Phase 4"
              className={errors.Title ? 'error' : ''}
              disabled={isSubmitting}
            />
            {errors.Title && <span className="error-message">{errors.Title}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="SourceEntityId">Source Entity (Affects) *</label>
              <select
                id="SourceEntityId"
                name="SourceEntityId"
                value={formData.SourceEntityId}
                onChange={handleChange}
                className={errors.SourceEntityId ? 'error' : ''}
                disabled={isSubmitting}
              >
                <option value="">-- Select source --</option>
                {entities.map(entity => (
                  <option key={entity.Id} value={entity.Id}>
                    [{entity.EntityType}] {entity.Title}
                  </option>
                ))}
              </select>
              {errors.SourceEntityId && <span className="error-message">{errors.SourceEntityId}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="TargetEntityId">Target Entity (Affected) *</label>
              <select
                id="TargetEntityId"
                name="TargetEntityId"
                value={formData.TargetEntityId}
                onChange={handleChange}
                className={errors.TargetEntityId ? 'error' : ''}
                disabled={isSubmitting}
              >
                <option value="">-- Select target --</option>
                {entities.map(entity => (
                  <option key={entity.Id} value={entity.Id}>
                    [{entity.EntityType}] {entity.Title}
                  </option>
                ))}
              </select>
              {errors.TargetEntityId && <span className="error-message">{errors.TargetEntityId}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="RuleType">Rule Type *</label>
            <select
              id="RuleType"
              name="RuleType"
              value={formData.RuleType}
              onChange={handleChange}
              disabled={isSubmitting}
            >
              <option value="Required">Required - Condition must be met or target is blocked</option>
              <option value="Contributes">Contributes - Adds value if met, unrealized if not</option>
              <option value="Blocks">Blocks - Reduces value if condition is true</option>
            </select>
            <small className="help-text">
              {formData.RuleType === 'Required' && 'If source condition is NOT met, target is completely blocked (achievable = 0)'}
              {formData.RuleType === 'Contributes' && 'If source condition is met, adds impact to target. If not met, shows as unrealized upside.'}
              {formData.RuleType === 'Blocks' && 'If source condition is TRUE, reduces target by impact amount.'}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="ConditionExpression">Condition Expression *</label>
            <input
              type="text"
              id="ConditionExpression"
              name="ConditionExpression"
              value={formData.ConditionExpression}
              onChange={handleChange}
              placeholder="e.g., Status == 'Met' or NumericValue >= 0.8"
              className={errors.ConditionExpression ? 'error' : ''}
              disabled={isSubmitting}
            />
            {errors.ConditionExpression && <span className="error-message">{errors.ConditionExpression}</span>}
            
            <div className="expression-examples">
              <strong>Examples:</strong>
              <div className="examples-grid">
                <div className="example-category">
                  <strong>Status:</strong>
                  {exampleExpressions.Status.map((exp, i) => (
                    <code key={i} onClick={() => setFormData(prev => ({...prev, ConditionExpression: exp}))}>
                      {exp}
                    </code>
                  ))}
                </div>
                <div className="example-category">
                  <strong>Score:</strong>
                  {exampleExpressions.Score.map((exp, i) => (
                    <code key={i} onClick={() => setFormData(prev => ({...prev, ConditionExpression: exp}))}>
                      {exp}
                    </code>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="ImpactValue">Impact Value</label>
              <input
                type="number"
                id="ImpactValue"
                name="ImpactValue"
                value={formData.ImpactValue}
                onChange={handleChange}
                step="0.01"
                placeholder="e.g., 3700"
                disabled={isSubmitting}
              />
              <small className="help-text">
                {formData.RuleType === 'Required' && 'Not used for Required rules'}
                {formData.RuleType === 'Contributes' && 'Amount to add/subtract from target'}
                {formData.RuleType === 'Blocks' && 'Amount to reduce from target'}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="ImpactUnit">Impact Unit</label>
              <select
                id="ImpactUnit"
                name="ImpactUnit"
                value={formData.ImpactUnit}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value="Absolute">Absolute (fixed number)</option>
                <option value="Percentage">Percentage (% of target)</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="Priority">Priority</label>
              <input
                type="number"
                id="Priority"
                name="Priority"
                value={formData.Priority}
                onChange={handleChange}
                min="1"
                max="100"
                disabled={isSubmitting}
              />
              <small className="help-text">Lower numbers evaluated first (1-100)</small>
            </div>

            <div className="form-group">
              <label>Rule Preview</label>
              <div className="rule-preview">
                {sourceEntity && targetEntity ? (
                  <>
                    <strong>{sourceEntity.Title}</strong>
                    <span className="arrow">→</span>
                    <span className={`rule-type ${formData.RuleType.toLowerCase()}`}>
                      {formData.RuleType}
                    </span>
                    <span className="arrow">→</span>
                    <strong>{targetEntity.Title}</strong>
                  </>
                ) : (
                  <span className="preview-placeholder">Select entities to preview</span>
                )}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="Description">Description (Optional)</label>
            <textarea
              id="Description"
              name="Description"
              value={formData.Description}
              onChange={handleChange}
              rows="2"
              placeholder="Additional notes about this rule"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-cancel" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRuleModal;